import * as Sentry from '@sentry/react';
import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { X, Loader as Loader2, CircleCheck as CheckCircle, CircleAlert as AlertCircle, Package, PackageCheck, Truck, Printer, TriangleAlert as AlertTriangle, Globe, Minus, Square, Circle as XCircle, Sparkles } from 'lucide-react';
import { PDFDocument } from 'pdf-lib';
import { Button } from '../ui/Button';
import type { ShopifyOrder, FinishOrderResponse } from '../../types/shopify';
import { formatCurrency } from '../../types/shopify';
import { supabase } from '../../lib/supabase';
import { classifyOrderIssue } from '../../utils/classifyOrderIssue';
import { validateShippingAddress } from '../../utils/addressParser';
import { useAiAddress } from '../../hooks/useAiAddress';

type ActionType = 'mark_ready' | 'finish_orders';
type ModalStage = 'confirm' | 'processing' | 'results';
type OrderResultStatus = 'pending' | 'processing' | 'success' | 'failed' | 'skipped';

interface OrderResult {
  orderId: string;
  shopifyOrderId: number;
  orderNumber: string;
  customerName: string;
  status: OrderResultStatus;
  trackingCode?: string;
  labelPdf?: string;
  error?: string;
  skippedReason?: string;
}

interface BulkOrderActionsModalProps {
  orders: ShopifyOrder[];
  actionType: ActionType;
  onClose: () => void;
  onComplete: () => void;
  getAuthHeaders: () => Promise<Record<string, string>>;
  adminId: string;
  userId: string;
}

function getEligibility(order: ShopifyOrder, actionType: ActionType): { eligible: boolean; reason?: string; warning?: string } {
  if (order.fulfillment_status === 'FULFILLED' || order.fulfilled_at) {
    return { eligible: false, reason: 'Already fulfilled' };
  }
  if (order.financial_status !== 'PAID' && order.financial_status !== 'PARTIALLY_REFUNDED') {
    return { eligible: false, reason: 'Not paid' };
  }

  if (actionType === 'mark_ready') {
    if (order.is_ready) {
      return { eligible: false, reason: 'Already ready' };
    }
  }

  if (actionType === 'finish_orders') {
    if (!order.shipping_address?.address1 || !order.shipping_address?.city || !order.shipping_address?.zip) {
      return { eligible: false, reason: 'Incomplete address' };
    }
    const addrCheck = validateShippingAddress(order.shipping_address);
    if (!addrCheck.valid) {
      return { eligible: false, reason: addrCheck.errors[0] || 'Address validation failed' };
    }
  }

  const warning = order.financial_status === 'PARTIALLY_REFUNDED' ? 'Partially refunded' : undefined;
  return { eligible: true, warning };
}

function getCountryCode(order: ShopifyOrder): string {
  return (order.shipping_address?.country_code || order.shipping_address?.country || 'NL')
    .toUpperCase()
    .replace('UK', 'GB');
}

const BulkOrderActionsModal: React.FC<BulkOrderActionsModalProps> = ({
  orders,
  actionType,
  onClose,
  onComplete,
  getAuthHeaders,
  adminId,
  userId,
}) => {
  const [stage, setStage] = useState<ModalStage>('confirm');
  const [results, setResults] = useState<OrderResult[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [mergedPdfUrl, setMergedPdfUrl] = useState<string | null>(null);
  const [refundAcknowledged, setRefundAcknowledged] = useState(false);
  const abortRef = useRef(false);
  const { normalizeAddress, checkAiEnabled } = useAiAddress();
  const [aiEnabled, setAiEnabled] = useState(false);

  useEffect(() => {
    checkAiEnabled().then(s => setAiEnabled(s.enabled && s.hasKey));
  }, [checkAiEnabled]);

  const eligibleOrders = useMemo(() => {
    return orders.map(order => ({
      order,
      ...getEligibility(order, actionType),
    }));
  }, [orders, actionType]);

  const eligibleCount = eligibleOrders.filter(o => o.eligible).length;
  const ineligibleCount = eligibleOrders.filter(o => !o.eligible).length;
  const hasPartiallyRefunded = eligibleOrders.some(o => o.eligible && o.warning);

  const summary = useMemo(() => {
    const succeeded = results.filter(r => r.status === 'success').length;
    const failed = results.filter(r => r.status === 'failed').length;
    const skipped = results.filter(r => r.status === 'skipped').length;
    return { succeeded, failed, skipped, total: results.length };
  }, [results]);

  const mergeLabelPdfs = useCallback(async (labelResults: OrderResult[]) => {
    const pdfsWithLabels = labelResults.filter(r => r.status === 'success' && r.labelPdf);
    if (pdfsWithLabels.length === 0) return;

    try {
      const mergedDoc = await PDFDocument.create();

      for (const result of pdfsWithLabels) {
        const pdfBytes = Uint8Array.from(atob(result.labelPdf!), c => c.charCodeAt(0));
        const srcDoc = await PDFDocument.load(pdfBytes);
        const pages = await mergedDoc.copyPages(srcDoc, srcDoc.getPageIndices());
        pages.forEach(page => mergedDoc.addPage(page));
      }

      const mergedBytes = await mergedDoc.save();
      const blob = new Blob([mergedBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      setMergedPdfUrl(url);
    } catch (err) {
      console.error('Failed to merge PDFs:', err);
    }
  }, []);

  const processMarkReady = useCallback(async () => {
    const eligible = eligibleOrders.filter(o => o.eligible);
    const initialResults: OrderResult[] = eligible.map(({ order }) => ({
      orderId: order.id,
      shopifyOrderId: order.shopify_order_id,
      orderNumber: order.order_number,
      customerName: order.customer_name || 'Unknown',
      status: 'pending' as OrderResultStatus,
    }));
    setResults(initialResults);
    setStage('processing');

    const updatedResults = [...initialResults];

    for (let i = 0; i < eligible.length; i++) {
      if (abortRef.current) break;
      setCurrentIndex(i);

      const { order } = eligible[i];
      updatedResults[i] = { ...updatedResults[i], status: 'processing' };
      setResults([...updatedResults]);

      try {
        const { data, error } = await supabase.rpc('update_shopify_order_ready_status', {
          p_order_id: order.id,
          p_admin_id: adminId,
          p_is_ready: true,
          p_user_id: userId,
        });

        if (error) throw error;
        const result = data as { success: boolean; error?: string };
        if (!result.success) throw new Error(result.error || 'Failed to mark ready');

        updatedResults[i] = { ...updatedResults[i], status: 'success' };
      } catch (err) {
        updatedResults[i] = {
          ...updatedResults[i],
          status: 'failed',
          error: err instanceof Error ? err.message : 'Unknown error',
        };
      }
      setResults([...updatedResults]);
    }

    setStage('results');
  }, [eligibleOrders, adminId, userId]);

  const processFinishOrders = useCallback(async () => {
    const eligible = eligibleOrders.filter(o => o.eligible);
    Sentry.addBreadcrumb({ category: 'bulk_finish', message: `Starting Bulk Finish for ${eligible.length} orders`, level: 'info' });
    const initialResults: OrderResult[] = eligible.map(({ order }) => ({
      orderId: order.id,
      shopifyOrderId: order.shopify_order_id,
      orderNumber: order.order_number,
      customerName: order.customer_name || 'Unknown',
      status: 'pending' as OrderResultStatus,
    }));
    setResults(initialResults);
    setStage('processing');

    const updatedResults = [...initialResults];

    for (let i = 0; i < eligible.length; i++) {
      if (abortRef.current) break;
      setCurrentIndex(i);

      const { order } = eligible[i];
      updatedResults[i] = { ...updatedResults[i], status: 'processing' };
      setResults([...updatedResults]);

      try {
        if (aiEnabled && order.shipping_address) {
          const addr = order.shipping_address;
          const cc = (addr.country_code || addr.country || 'NL').toUpperCase().replace('UK', 'GB');
          const aiRes = await normalizeAddress({
            name: addr.name || '',
            street: addr.address1 || '',
            addressExtra: addr.address2 || '',
            postalCode: addr.zip || '',
            city: addr.city || '',
            countryCode: cc,
            region: addr.province || addr.province_code || '',
            phone: addr.phone || '',
          }, undefined, {
            shopifyOrderId: order.shopify_order_id,
            orderNumber: order.order_number || undefined,
            source: 'bulk_finish',
          });

          if (aiRes?.diagnostics?.issues?.some(i => i.severity === 'error')) {
            const errorIssues = aiRes.diagnostics.issues.filter(i => i.severity === 'error');
            const issueMsg = `AI detected address errors: ${errorIssues.map(i => `${i.field}: ${i.message}`).join('; ')}`;
            if (order.order_number) {
              try {
                await supabase.rpc('upsert_order_issue', {
                  p_admin_id: adminId,
                  p_order_reference: order.order_number,
                  p_shopify_order_id: order.shopify_order_id ?? null,
                  p_client_name: order.customer_name ?? null,
                  p_recipient_address: order.shipping_address ?? {},
                  p_issue_type: 'address_invalid',
                  p_issue_description: issueMsg,
                  p_error_code: null,
                  p_source: 'ai_bulk_validation',
                });
              } catch { /* non-critical */ }
            }
          }
        }

        if (!order.is_ready) {
          const { data, error } = await supabase.rpc('update_shopify_order_ready_status', {
            p_order_id: order.id,
            p_admin_id: adminId,
            p_is_ready: true,
            p_user_id: userId,
          });

          if (error) throw error;
          const result = data as { success: boolean; error?: string };
          if (!result.success) throw new Error(result.error || 'Failed to auto-mark as ready');
        }

        const headers = await getAuthHeaders();
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/shopify-finish-order`,
          {
            method: 'POST',
            headers,
            body: JSON.stringify({ shopifyOrderId: order.shopify_order_id }),
          }
        );

        const text = await response.text();
        let data: FinishOrderResponse | null = null;
        try {
          data = text ? (JSON.parse(text) as FinishOrderResponse) : null;
        } catch {
          throw new Error(response.ok ? 'Invalid response' : `HTTP ${response.status}`);
        }

        if (!response.ok || !data?.success) {
          throw new Error(data?.error || `HTTP ${response.status}`);
        }

        updatedResults[i] = {
          ...updatedResults[i],
          status: 'success',
          trackingCode: data.trackingCode,
          labelPdf: data.labelPdf,
        };

        if (order.order_number) {
          try {
            await supabase.rpc('resolve_order_issue_by_reference', {
              p_admin_id: adminId,
              p_order_reference: order.order_number,
            });
          } catch (resolveErr) {
            Sentry.captureException(resolveErr, { tags: { operation: 'resolve_order_issue' }, extra: { orderNumber: order.order_number } });
          }
        }
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Unknown error';
        updatedResults[i] = {
          ...updatedResults[i],
          status: 'failed',
          error: errorMsg,
        };

        Sentry.addBreadcrumb({ category: 'bulk_finish', message: `Order ${order.order_number} failed: ${errorMsg}`, level: 'error' });

        if (order.order_number) {
          try {
            await supabase.rpc('upsert_order_issue', {
              p_admin_id: adminId,
              p_order_reference: order.order_number,
              p_shopify_order_id: order.shopify_order_id ?? null,
              p_client_name: order.customer_name ?? null,
              p_recipient_address: order.shipping_address ?? {},
              p_issue_type: classifyOrderIssue(errorMsg),
              p_issue_description: errorMsg,
              p_error_code: null,
              p_source: 'bulk_finish_orders',
            });
          } catch (upsertErr) {
            console.error('Failed to record order issue:', upsertErr);
            Sentry.captureException(upsertErr, { tags: { operation: 'upsert_order_issue' }, extra: { orderNumber: order.order_number, errorMsg } });
          }
        }
      }

      setResults([...updatedResults]);

      if (i < eligible.length - 1 && !abortRef.current) {
        await new Promise(r => setTimeout(r, 400));
      }
    }

    await mergeLabelPdfs(updatedResults);
    setStage('results');
  }, [eligibleOrders, adminId, userId, getAuthHeaders, mergeLabelPdfs, aiEnabled, normalizeAddress]);

  const handleConfirm = () => {
    abortRef.current = false;
    if (actionType === 'mark_ready') {
      processMarkReady();
    } else {
      processFinishOrders();
    }
  };

  const handleStop = () => {
    abortRef.current = true;
  };

  const handlePrintAllLabels = () => {
    if (mergedPdfUrl) {
      window.open(mergedPdfUrl, '_blank');
    }
  };

  const handleClose = () => {
    if (mergedPdfUrl) URL.revokeObjectURL(mergedPdfUrl);
    if (stage === 'results') {
      onComplete();
    }
    onClose();
  };

  const title = actionType === 'mark_ready' ? 'Bulk Mark Ready' : 'Bulk Finish Orders';
  const titleIcon = actionType === 'mark_ready' ? CheckCircle : PackageCheck;
  const TitleIcon = titleIcon;
  const gradientClass = actionType === 'mark_ready'
    ? 'from-green-500 to-emerald-600'
    : 'from-green-600 to-emerald-600';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${gradientClass} flex items-center justify-center`}>
              <TitleIcon className="h-5 w-5 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">{title}</h2>
                {aiEnabled && actionType === 'finish_orders' && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300">
                    <Sparkles className="h-3 w-3" /> AI
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {stage === 'confirm' && `${eligibleCount} of ${orders.length} orders eligible`}
                {stage === 'processing' && `Processing ${currentIndex + 1} of ${eligibleCount}...`}
                {stage === 'results' && `${summary.succeeded} succeeded, ${summary.failed} failed`}
              </p>
            </div>
          </div>
          <button onClick={handleClose} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {stage === 'confirm' && (
            <ConfirmStage
              eligibleOrders={eligibleOrders}
              actionType={actionType}
              eligibleCount={eligibleCount}
              ineligibleCount={ineligibleCount}
              hasPartiallyRefunded={hasPartiallyRefunded}
              refundAcknowledged={refundAcknowledged}
              onRefundAcknowledge={setRefundAcknowledged}
            />
          )}

          {stage === 'processing' && (
            <ProcessingStage
              results={results}
              currentIndex={currentIndex}
              totalCount={eligibleCount}
              actionType={actionType}
            />
          )}

          {stage === 'results' && (
            <ResultsStage
              results={results}
              summary={summary}
              actionType={actionType}
            />
          )}
        </div>

        <div className="p-5 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
          {stage === 'confirm' && (
            <div className="flex gap-3">
              <Button variant="secondary" onClick={handleClose} className="flex-1">Cancel</Button>
              <Button
                variant="primary"
                onClick={handleConfirm}
                disabled={eligibleCount === 0 || (hasPartiallyRefunded && !refundAcknowledged)}
                className={`flex-1 bg-gradient-to-r ${gradientClass} hover:brightness-110`}
              >
                <TitleIcon className="h-4 w-4 mr-2" />
                {actionType === 'mark_ready'
                  ? `Mark ${eligibleCount} Ready`
                  : `Finish ${eligibleCount} Orders`
                }
              </Button>
            </div>
          )}

          {stage === 'processing' && (
            <div className="flex gap-3">
              <Button
                variant="secondary"
                onClick={handleStop}
                className="flex-1 border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
              >
                <Square className="h-4 w-4 mr-2" />
                Stop
              </Button>
            </div>
          )}

          {stage === 'results' && (
            <div className="flex gap-3">
              {actionType === 'finish_orders' && mergedPdfUrl && (
                <Button
                  variant="primary"
                  onClick={handlePrintAllLabels}
                  className="flex-1"
                >
                  <Printer className="h-4 w-4 mr-2" />
                  Print All Labels ({summary.succeeded})
                </Button>
              )}
              <Button
                variant="secondary"
                onClick={handleClose}
                className={actionType === 'finish_orders' && mergedPdfUrl ? '' : 'flex-1'}
              >
                Close
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ConfirmStage: React.FC<{
  eligibleOrders: Array<{ order: ShopifyOrder; eligible: boolean; reason?: string; warning?: string }>;
  actionType: ActionType;
  eligibleCount: number;
  ineligibleCount: number;
  hasPartiallyRefunded: boolean;
  refundAcknowledged: boolean;
  onRefundAcknowledge: (v: boolean) => void;
}> = ({ eligibleOrders, actionType, eligibleCount, ineligibleCount, hasPartiallyRefunded, refundAcknowledged, onRefundAcknowledge }) => (
  <>
    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 mb-4">
      <h4 className="text-sm font-semibold text-blue-800 dark:text-blue-300 mb-2">
        {actionType === 'mark_ready' ? 'What will happen:' : 'What will happen for each order:'}
      </h4>
      <ol className="text-xs text-blue-700 dark:text-blue-400 space-y-1 list-decimal list-inside">
        {actionType === 'mark_ready' ? (
          <>
            <li>Mark each eligible order as "Ready"</li>
            <li>Orders already ready or not paid will be skipped</li>
          </>
        ) : (
          <>
            <li>Auto-mark as Ready (if not already)</li>
            <li>Generate PostNL shipping label</li>
            <li>Create fulfillment in Shopify</li>
            <li>Push tracking info to customer</li>
            <li>Combine all labels into one printable PDF</li>
          </>
        )}
      </ol>
    </div>

    {hasPartiallyRefunded && (
      <div className="mb-4 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl">
        <div className="flex items-start gap-3 mb-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
              Some orders are partially refunded
            </p>
            <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
              By proceeding, you confirm you have reviewed these orders and want to continue despite the partial refund.
            </p>
          </div>
        </div>
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={refundAcknowledged}
            onChange={(e) => onRefundAcknowledge(e.target.checked)}
            className="w-4 h-4 rounded border-amber-300 text-amber-600 focus:ring-amber-500"
          />
          <span className="text-sm font-medium text-amber-800 dark:text-amber-300">
            I understood, proceed with partially refunded orders
          </span>
        </label>
      </div>
    )}

    {ineligibleCount > 0 && (
      <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg flex items-start gap-2">
        <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
        <span className="text-sm text-amber-700 dark:text-amber-300">
          {ineligibleCount} order{ineligibleCount !== 1 ? 's' : ''} will be skipped (see reasons below)
        </span>
      </div>
    )}

    <div className="space-y-1.5 max-h-[350px] overflow-y-auto">
      {eligibleOrders.map(({ order, eligible, reason, warning }) => (
        <div
          key={order.id}
          className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
            eligible
              ? 'bg-green-50 dark:bg-green-900/10'
              : 'bg-gray-50 dark:bg-gray-900/30 opacity-70'
          }`}
        >
          {eligible ? (
            <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400 flex-shrink-0" />
          ) : (
            <Minus className="h-4 w-4 text-gray-400 flex-shrink-0" />
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {order.order_number}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
                {order.customer_name || 'Unknown'}
              </span>
              {eligible && warning && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800">
                  <AlertTriangle className="h-2.5 w-2.5" />
                  {warning}
                </span>
              )}
            </div>
            {!eligible && reason && (
              <span className="text-xs text-amber-600 dark:text-amber-400">{reason}</span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {actionType === 'finish_orders' && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                <Globe className="h-3 w-3 inline mr-1" />
                {getCountryCode(order)}
              </span>
            )}
            <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
              {formatCurrency(order.total_price, order.currency)}
            </span>
          </div>
        </div>
      ))}
    </div>
  </>
);

const ProcessingStage: React.FC<{
  results: OrderResult[];
  currentIndex: number;
  totalCount: number;
  actionType: ActionType;
}> = ({ results, currentIndex, totalCount, actionType }) => {
  const percentage = totalCount > 0 ? ((currentIndex + 1) / totalCount) * 100 : 0;

  return (
    <>
      <div className="mb-5">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {actionType === 'mark_ready' ? 'Marking orders ready...' : 'Generating labels & fulfilling...'}
          </span>
          <span className="text-sm font-bold text-gray-900 dark:text-gray-100">
            {Math.round(percentage)}%
          </span>
        </div>
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 overflow-hidden">
          <div
            className="bg-gradient-to-r from-green-500 to-emerald-500 h-2.5 rounded-full transition-all duration-300"
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>

      <div className="space-y-1.5 max-h-[400px] overflow-y-auto">
        {results.map((result, idx) => (
          <div
            key={result.orderId}
            className={`flex items-center gap-3 p-3 rounded-lg ${
              result.status === 'processing'
                ? 'bg-cyan-50 dark:bg-cyan-900/15 ring-1 ring-cyan-200 dark:ring-cyan-800'
                : result.status === 'success'
                  ? 'bg-green-50 dark:bg-green-900/10'
                  : result.status === 'failed'
                    ? 'bg-red-50 dark:bg-red-900/10'
                    : 'bg-gray-50 dark:bg-gray-900/20'
            }`}
          >
            <StatusIcon status={result.status} />
            <div className="flex-1 min-w-0">
              <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {result.orderNumber}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                {result.customerName}
              </span>
              {result.status === 'failed' && result.error && (
                <p className="text-xs text-red-600 dark:text-red-400 mt-0.5 truncate" title={result.error}>
                  {result.error}
                </p>
              )}
              {result.status === 'success' && result.trackingCode && (
                <p className="text-xs text-green-600 dark:text-green-400 mt-0.5 font-mono">
                  {result.trackingCode}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

const ResultsStage: React.FC<{
  results: OrderResult[];
  summary: { succeeded: number; failed: number; skipped: number; total: number };
  actionType: ActionType;
}> = ({ results, summary, actionType }) => (
  <>
    <div className="grid grid-cols-3 gap-3 mb-5">
      <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4 text-center">
        <div className="text-2xl font-bold text-green-700 dark:text-green-300">{summary.succeeded}</div>
        <div className="text-xs text-green-600 dark:text-green-400 font-medium">Succeeded</div>
      </div>
      <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-4 text-center">
        <div className="text-2xl font-bold text-red-700 dark:text-red-300">{summary.failed}</div>
        <div className="text-xs text-red-600 dark:text-red-400 font-medium">Failed</div>
      </div>
      <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 text-center">
        <div className="text-2xl font-bold text-gray-700 dark:text-gray-300">{summary.total}</div>
        <div className="text-xs text-gray-600 dark:text-gray-400 font-medium">Total</div>
      </div>
    </div>

    <div className="space-y-1.5 max-h-[350px] overflow-y-auto">
      {results.map((result) => (
        <div
          key={result.orderId}
          className={`flex items-center gap-3 p-3 rounded-lg ${
            result.status === 'success'
              ? 'bg-green-50 dark:bg-green-900/10'
              : result.status === 'failed'
                ? 'bg-red-50 dark:bg-red-900/10'
                : 'bg-gray-50 dark:bg-gray-900/20'
          }`}
        >
          <StatusIcon status={result.status} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {result.orderNumber}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {result.customerName}
              </span>
            </div>
            {result.status === 'success' && result.trackingCode && (
              <div className="flex items-center gap-1.5 mt-0.5">
                <Truck className="h-3 w-3 text-green-600 dark:text-green-400" />
                <span className="text-xs text-green-600 dark:text-green-400 font-mono">
                  {result.trackingCode}
                </span>
              </div>
            )}
            {result.status === 'failed' && result.error && (
              <p className="text-xs text-red-600 dark:text-red-400 mt-0.5" title={result.error}>
                {result.error.length > 120 ? result.error.slice(0, 120) + '...' : result.error}
              </p>
            )}
            {result.status === 'skipped' && result.skippedReason && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{result.skippedReason}</p>
            )}
          </div>
        </div>
      ))}
    </div>
  </>
);

const StatusIcon: React.FC<{ status: OrderResultStatus }> = ({ status }) => {
  switch (status) {
    case 'processing':
      return <Loader2 className="h-4 w-4 text-cyan-600 dark:text-cyan-400 animate-spin flex-shrink-0" />;
    case 'success':
      return <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400 flex-shrink-0" />;
    case 'failed':
      return <XCircle className="h-4 w-4 text-red-600 dark:text-red-400 flex-shrink-0" />;
    case 'skipped':
      return <Minus className="h-4 w-4 text-gray-400 flex-shrink-0" />;
    default:
      return <Package className="h-4 w-4 text-gray-400 flex-shrink-0" />;
  }
};

export default BulkOrderActionsModal;
